import type {
  CancelReaderResponse,
  CreatePaymentIntentResponse,
  PaymentFailure,
  PaymentIntentResponse,
} from '@/shared/types/api';
import {
  api,
  type BreadcrumbInput,
  instrument,
  requireSessionId,
  unwrap,
} from '../client';
import type { ParsedError } from '../error-parser';
import type { components } from '../v1.gen';

type Currency = components['schemas']['Currency'];

// The v1 action routes (send-to-reader, confirm, capture, hold) answer a
// failed payment action with one of these statuses, and put the failure in
// details.failure of the error body. Payment.js reports these failures to
// Sentry with the level of their cause.
const PAYMENT_ACTION_FAILURE_STATUSES: readonly number[] = [402, 404, 409];

export type PaymentActionError = ParsedError & { failure?: PaymentFailure };

interface PaymentActionErrorBody {
  code?: unknown;
  details?: { failure?: unknown } | null;
}

function isPaymentFailure(value: unknown): value is PaymentFailure {
  const candidate = value as Partial<PaymentFailure> | null | undefined;
  return (
    typeof candidate?.category === 'string' &&
    typeof candidate.code === 'string'
  );
}

// A 404 or 409 from send-to-reader means that the reader cannot take this
// intent, for example an unknown reader or a canceled intent. The visitor
// sees the reader text for each of them, also for a body without a failure.
function withPaymentActionFailure(
  error: unknown,
  isSendToReader: boolean
): unknown {
  const response = (error as ParsedError | null)?.context?.response;
  const status = response?.status?.code;
  if (
    status === undefined ||
    !PAYMENT_ACTION_FAILURE_STATUSES.includes(status)
  ) {
    return error;
  }
  const body = response?.json as PaymentActionErrorBody | null | undefined;
  const failure = body?.details?.failure;
  if (isPaymentFailure(failure)) {
    (error as PaymentActionError).failure = failure;
  } else if (isSendToReader && status !== 402) {
    (error as PaymentActionError).failure = {
      category: 'reader_error',
      code: typeof body?.code === 'string' ? body.code : `http_${status}`,
      message: null,
      decline_code: null,
      stripe_code: null,
      attempt_id: null,
    };
  }
  return error;
}

function paymentAction<T>(
  pending: Promise<{ data?: T }>,
  isSendToReader = false
): Promise<T> {
  return unwrap(pending).catch((error: unknown) => {
    throw withPaymentActionFailure(error, isSendToReader);
  });
}

export function createPaymentIntent(
  amount: number,
  currency: string,
  partnerStripeId: string,
  idempotencyKey: string | null = null,
  checkoutKey: string | null = null,
  copyAmountsCents: readonly number[] | null = null
): Promise<CreatePaymentIntentResponse> {
  return instrument<CreatePaymentIntentResponse>(
    {
      span: 'api.create_payment_intent',
      errorTag: 'payment_intents_create_payment_intent',
      category: 'payment',
      tags: { 'payment.amount': amount, 'payment.currency': currency },
      before: {
        message: 'Creating payment intent',
        data: {
          amount,
          currency,
          has_idempotency_key: Boolean(idempotencyKey),
          expected_copies: copyAmountsCents?.length ?? null,
        },
      },
      after: (r) => ({
        message: 'Payment intent created',
        // Preserve full Stripe IDs in Sentry breadcrumbs; they are the join keys
        // between kiosk logs, backend rows, and Stripe dashboard events.
        data: { payment_intent_id: r?.id },
      }),
      errorExtra: { amount, currency },
    },
    () => {
      const sessionId = requireSessionId();
      return unwrap(
        api.POST('/v1/payment-intents', {
          body: {
            session_id: sessionId,
            type: 'terminal',
            partner_stripe_id: partnerStripeId,
            amount,
            currency: (currency?.toLowerCase() ?? 'eur') as Currency,
            // Idempotency key prevents duplicate intents on network retries.
            // Backend forwards this to Stripe's Idempotency-Key header.
            ...(idempotencyKey && { idempotency_key: idempotencyKey }),
            // Order identity: a second order on a paid session needs its own key.
            ...(checkoutKey && { checkout_key: checkoutKey }),
            // The declared copies: the reader holds the amount, and the backend
            // captures only the printed copies. An older backend ignores the two
            // fields and answers without `capture`.
            ...(copyAmountsCents &&
              copyAmountsCents.length > 0 && {
                expected_copies: copyAmountsCents.length,
                copy_amounts_cents: [...copyAmountsCents],
              }),
          },
        })
      );
    }
  );
}

export function sendPaymentIntentToReader(
  paymentIntentId: string,
  partnerStripeId: string,
  readerId: string | null
): Promise<PaymentIntentResponse> {
  return instrument<PaymentIntentResponse>(
    {
      span: 'api.send_payment_intent_to_reader',
      errorTag: 'payment_intents_send_payment_intent_to_reader',
      category: 'payment',
      tags: { 'payment.reader_id': readerId },
      before: {
        message: 'Sending payment intent to reader',
        // Preserve full operational IDs for Sentry-to-Stripe correlation.
        data: { payment_intent_id: paymentIntentId, reader_id: readerId },
      },
      after: () => ({
        message: 'Payment intent sent to reader',
        data: { reader_id: readerId },
      }),
      errorExtra: { payment_intent_id: paymentIntentId, reader_id: readerId },
      skipSentryOn: [...PAYMENT_ACTION_FAILURE_STATUSES],
    },
    () =>
      paymentAction(
        api.POST('/v1/payment-intents/{payment_intent_id}/send-to-reader', {
          params: { path: { payment_intent_id: paymentIntentId } },
          body: {
            partner_stripe_id: partnerStripeId,
            reader_id: readerId ?? null,
          },
        }),
        true
      )
  );
}

export function cancelPaymentOnReader(
  readerId: string | null,
  partnerStripeId: string,
  // ``sessionId`` and ``paymentIntentId`` let the backend resolver
  // short-circuit its per-Connect-account probe loop. Without either,
  // backend has to scan every connected partner account, which is
  // slow and emits spurious ``resource_missing`` Sentry noise.
  // ``releaseHold``: the kiosk will not accept this payment. The backend
  // then releases a held amount of ``paymentIntentId`` that it did not accept.
  options: {
    sessionId?: string;
    paymentIntentId?: string;
    releaseHold?: boolean;
  } = {}
): Promise<CancelReaderResponse> {
  const releaseHold = Boolean(options.releaseHold && options.paymentIntentId);
  return instrument<CancelReaderResponse>(
    {
      span: 'api.cancel_payment_on_reader',
      errorTag: 'payment_intents_cancel_reader_action',
      category: 'payment',
      tags: { 'payment.reader_id': readerId },
      before: {
        message: 'Cancelling payment on reader',
        data: { reader_id: readerId },
      },
      after: () => ({
        message: 'Payment cancelled on reader',
        data: { reader_id: readerId },
      }),
      // Payment.js reports a failed reset, with the reader failure of a 409
      // (a backend of a previous release answers a reader fault with a 500).
      skipSentryCapture: true,
    },
    () =>
      paymentAction(
        api.POST('/v1/payment-intents/cancel-reader', {
          body: {
            reader_id: readerId ?? null,
            partner_stripe_id: partnerStripeId,
            session_id: options.sessionId ?? null,
            payment_intent_id: options.paymentIntentId ?? null,
            ...(releaseHold && { release_hold: true }),
          },
        })
      )
  );
}

export function checkPaymentIntentStatus(
  paymentIntentId: string,
  partnerStripeId: string
): Promise<PaymentIntentResponse> {
  // No before/after breadcrumbs — polled frequently (every 1s).
  return instrument<PaymentIntentResponse>(
    {
      span: 'api.check_payment_intent_status',
      errorTag: 'payment_intents_get_payment_intent',
      category: 'payment',
      tags: { 'payment.intent_id': paymentIntentId },
      errorExtra: { payment_intent_id: paymentIntentId },
    },
    () =>
      // silent drops only per-poll success telemetry; failures still reach Sentry.
      unwrap(
        api.GET('/v1/payment-intents/{payment_intent_id}', {
          params: {
            path: { payment_intent_id: paymentIntentId },
            query: { partner_stripe_id: partnerStripeId || undefined },
          },
          silent: true,
        })
      )
  );
}

export function capturePaymentIntent(
  paymentIntentId: string,
  partnerStripeId: string,
  checkoutKey: string | null = null
): Promise<PaymentIntentResponse> {
  return instrument<PaymentIntentResponse>(
    {
      span: 'api.capture_payment_intent',
      errorTag: 'payment_intents_capture_payment_intent',
      category: 'payment',
      tags: { 'payment.intent_id': paymentIntentId },
      before: {
        message: 'Capturing payment intent',
        data: { payment_intent_id: paymentIntentId },
      },
      after: () => ({
        message: 'Payment intent captured',
        data: { payment_intent_id: paymentIntentId },
      }),
      errorExtra: { payment_intent_id: paymentIntentId },
      skipSentryOn: [...PAYMENT_ACTION_FAILURE_STATUSES],
    },
    () =>
      paymentAction(
        api.POST('/v1/payment-intents/{payment_intent_id}/capture', {
          params: { path: { payment_intent_id: paymentIntentId } },
          body: {
            partner_stripe_id: partnerStripeId,
            ...(checkoutKey && { checkout_key: checkoutKey }),
          },
        })
      )
  );
}

// The kiosk accepts the held payment of a declared order, in place of the
// capture. The backend captures only the printed copies after the last copy
// of the order has a result. The answer is `requires_capture`, or
// `succeeded` when the order settled before a repeated call.
export function holdPaymentIntent(
  paymentIntentId: string,
  partnerStripeId: string,
  checkoutKey: string | null = null
): Promise<PaymentIntentResponse> {
  return instrument<PaymentIntentResponse>(
    {
      span: 'api.hold_payment_intent',
      errorTag: 'payment_intents_hold_payment_intent',
      category: 'payment',
      tags: { 'payment.intent_id': paymentIntentId },
      before: {
        message: 'Accept the held payment intent',
        data: { payment_intent_id: paymentIntentId },
      },
      after: (r: PaymentIntentResponse): BreadcrumbInput => ({
        message: 'Held payment intent accepted',
        data: { payment_intent_id: paymentIntentId, status: r?.status },
      }),
      errorExtra: { payment_intent_id: paymentIntentId },
      skipSentryOn: [...PAYMENT_ACTION_FAILURE_STATUSES],
    },
    () =>
      paymentAction(
        api.POST('/v1/payment-intents/{payment_intent_id}/hold', {
          params: { path: { payment_intent_id: paymentIntentId } },
          body: {
            partner_stripe_id: partnerStripeId,
            ...(checkoutKey && { checkout_key: checkoutKey }),
          },
        })
      )
  );
}
